import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Building2, ChevronRight, FileText, Package, RefreshCw, ShieldAlert, ShieldCheck, Users } from 'lucide-react';
import { userService } from '../../services/api';
import { dashboardService, AdminDashboardSummary } from '../../services/dashboardService';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import DismissibleError from '../DismissibleError';
import { EmptyTableState, RecordsTableCard, StatusBadge } from '../records';

interface DashboardStats {
  total_users: number;
  active_users: number;
  admin_users: number;
  superuser_count: number;
  total_roles: number;
  default_role: string | null;
}

const value = (input: unknown, fallback = '-'): string =>
  input === null || input === undefined || input === '' ? fallback : String(input);

const formatDateTime = (input?: string): string => {
  if (!input) return '-';
  const date = new Date(input);
  return Number.isNaN(date.getTime()) ? input : date.toLocaleString();
};

const friendlyAction = (input?: string): string => {
  const key = value(input, '').toLowerCase();
  const labels: Record<string, string> = {
    user_login_failed: 'Failed Login',
    role_permission_updated: 'Role Permission Updated',
    backup_completed: 'Backup Completed',
    security_event: 'Security Event',
  };
  return labels[key] || value(input).replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [summary, setSummary] = useState<AdminDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const { error, setError, clearError, getErrorMessage } = useErrorHandler();

  const loadDashboardStats = React.useCallback(async () => {
    try {
      setLoading(true);
      clearError();
      const [userResponse, roleStats, adminSummary] = await Promise.all([
        userService.getStats(),
        userService.getRoleStats(),
        dashboardService.getAdminSummary(),
      ]);
      setStats({ ...userResponse, ...roleStats });
      setSummary(adminSummary);
    } catch (err) {
      setError(err as any);
    } finally {
      setLoading(false);
    }
  }, [clearError, setError]);

  useEffect(() => { void loadDashboardStats(); }, [loadDashboardStats]);

  if (loading) return <div className="loading-state"><span className="loading-mark" />Loading operational overview…</div>;

  const metrics = [
    { label: 'Total Users', value: summary?.user_account_summary?.total_users ?? stats?.total_users ?? 0, note: 'Registered accounts', icon: Users, tone: 'blue' },
    { label: 'Active Users', value: summary?.user_account_summary?.active_users ?? stats?.active_users ?? 0, note: 'With platform access', icon: Activity, tone: 'green' },
    { label: 'Recent Activity', value: summary?.recent_activity?.length ?? 0, note: 'Latest audit events', icon: ShieldCheck, tone: 'purple' },
    { label: 'Security Alerts', value: summary?.security_alert_summary?.security_events ?? 0, note: 'Security events', icon: ShieldAlert, tone: 'neutral' },
  ];

  return (
    <div className="admin-dashboard">
      <div className="page-heading">
        <div><h1>Administrator Dashboard</h1><p>System governance and security overview.</p></div>
        <button className="btn btn-secondary" onClick={loadDashboardStats}><RefreshCw size={14} />Refresh data</button>
      </div>

      {error && <DismissibleError message={getErrorMessage()} onDismiss={clearError} onRetry={loadDashboardStats} />}

      <div className="stats-grid">
        {metrics.map(({ label, value, note, icon: Icon, tone }) => <article className="stat-card" key={label}><span className={`metric-icon ${tone}`}><Icon size={20} /></span><div><span className="stat-label">{label}</span><strong className="stat-number">{value}</strong><span className="stat-note">{note}</span></div></article>)}
      </div>

      <div className="dashboard-grid">
        <RecordsTableCard
          title="Recent Activity"
          description="Latest system audit events."
          meta={`${summary?.recent_activity?.length || 0} events`}
          actions={<Link to="/admin-dashboard/audit-logs">Open trail <ChevronRight size={12} /></Link>}
        >
          <table className="records-table">
            <thead><tr><th>Date / Time</th><th>User</th><th>Action</th><th>Object / Module</th><th>Status</th></tr></thead>
            <tbody>
              {(!summary?.recent_activity || summary.recent_activity.length === 0) ? (
                <tr><td colSpan={5}><EmptyTableState title="No recent activity" description="Audit events will appear here when available." icon={<Package size={22} aria-hidden="true" />} /></td></tr>
              ) : summary.recent_activity.slice(0, 6).map((activity: any) => (
                <tr key={activity.id || `${activity.action}-${activity.timestamp}`}>
                  <td>{formatDateTime(activity.timestamp || activity.created_at)}</td>
                  <td>{value(activity.actor_username || activity.actor)}</td>
                  <td>{friendlyAction(activity.action)}</td>
                  <td>{value(activity.affected_entity || activity.module)} {activity.entity_id ? `#${activity.entity_id}` : ''}</td>
                  <td><StatusBadge status={activity.status || activity.result || 'recorded'} label={value(activity.status || activity.result, 'Recorded')} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </RecordsTableCard>

        <aside className="dashboard-side">
          <section className="panel quick-actions"><div className="section-heading"><div><h2>Quick Actions</h2></div></div>
            <Link to="/admin-dashboard/users"><span className="quick-icon"><Users size={14} /></span><span><strong>Create or update user</strong><small>Manage verified identities</small></span><ChevronRight size={13} /></Link>
            <Link to="/admin-dashboard/departments"><span className="quick-icon"><Building2 size={14} /></span><span><strong>Manage departments</strong><small>Maintain assignment options</small></span><ChevronRight size={13} /></Link>
            <Link to="/admin-dashboard/roles"><span className="quick-icon"><ShieldCheck size={14} /></span><span><strong>Review access roles</strong><small>Audit role permissions</small></span><ChevronRight size={13} /></Link>
            <Link to="/admin-dashboard/audit-logs"><span className="quick-icon"><FileText size={14} /></span><span><strong>Review audit trail</strong><small>Filter immutable events</small></span><ChevronRight size={13} /></Link>
            <Link to="/admin-dashboard/enter/logistics"><span className="quick-icon"><Package size={14} /></span><span><strong>Enter Logistics Workspace</strong><small>Select department and review logistics workflows</small></span><ChevronRight size={13} /></Link>
            <Link to="/admin-dashboard/enter/accountable"><span className="quick-icon"><Users size={14} /></span><span><strong>Enter Accountable Workspace</strong><small>Select department and review accountability workflows</small></span><ChevronRight size={13} /></Link>
            <Link to="/inspector-dashboard"><span className="quick-icon"><FileText size={14} /></span><span><strong>Enter Inspector Workspace</strong><small>Review inspection workflows</small></span><ChevronRight size={13} /></Link>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default AdminDashboard;
